import React from 'react';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { DemoListing } from './demoListing';

const euro = new Intl.NumberFormat('de-DE');

export interface TelegramMessageProps {
  listing: DemoListing;
  className?: string;
}

/**
 * One bot message as Telegram renders it: the real notification text layout
 * (portal, bold title, facts, address, link preview) plus the inline keyboard.
 * Colours come from the `--tg-*` variables in `styles/telegram-preview.css`,
 * which mimic Telegram's own light/dark look rather than the app palette.
 * Purely presentational: the "buttons" are <span>s.
 */
export const TelegramMessage: React.FC<TelegramMessageProps> = ({ listing, className }) => (
  <div className={cn('flex flex-col gap-1', className)}>
    <div className="tg-bubble rounded-[14px] rounded-bl-[4px] px-3 pb-1.5 pt-2 text-[13px] leading-[1.35]">
      <p>{listing.portal}</p>
      <p className="font-semibold">{listing.title}</p>
      <p>Zimmer: {listing.rooms}</p>
      <p>{listing.area} m²</p>
      <p className="tabular">Gesamtmiete: {euro.format(listing.rent)} €</p>
      {listing.coldRent != null && <p className="tabular">Kaltmiete: {euro.format(listing.coldRent)} €</p>}
      <p className="mt-2">
        PLZ: {listing.zip} ({listing.city})
      </p>
      <p>
        <span aria-hidden="true">📍 </span>
        {listing.address}
      </p>
      <div className="tg-preview-card mt-2 rounded-[6px] border-l-[3px] py-1 pl-2 pr-1">
        <p className="tg-preview-title font-semibold">{listing.portal}</p>
        <p className="line-clamp-2 font-medium">
          Wohnung {listing.area} m² {euro.format(listing.coldRent ?? listing.rent)} € zur Miete
        </p>
      </div>
      <p className="tg-meta mt-0.5 text-right text-[11px]">{listing.sentAt}</p>
    </div>
    <div className="grid grid-cols-2 gap-1 text-[12px] font-medium">
      <TgButton>
        <span aria-hidden="true">🏠 </span>Exposé
      </TgButton>
      <TgButton>
        <span aria-hidden="true">📝 </span>Jetzt bewerben
      </TgButton>
      <TgButton className="col-span-2">
        <span aria-hidden="true">📍 </span>Google Maps
      </TgButton>
    </div>
  </div>
);

const TgButton: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <span className={cn('tg-button relative rounded-[10px] px-2 py-1.5 text-center', className)}>
    {children}
    <ArrowUpRight className="absolute right-1 top-1 h-2.5 w-2.5" aria-hidden="true" />
  </span>
);

/** Telegram chat chrome (header with the bot avatar, wallpaper) around bot messages. */
export const TelegramChat: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="tg-chat flex h-full flex-col">
    <div className="tg-header flex items-center gap-2.5 px-3 py-2" aria-hidden="true">
      <ArrowLeft className="h-4 w-4 shrink-0" />
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
        <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" className="h-7 w-7" />
      </span>
      <span className="min-w-0 leading-tight">
        <span className="block truncate text-[14px] font-semibold">Schneller-Wohnen</span>
        <span className="tg-meta block text-[11px]">Bot</span>
      </span>
    </div>
    {/* justify-end + overflow-hidden: older messages are cut off at the top, like a scrolled chat. */}
    <div className="tg-wallpaper flex min-h-0 flex-1 flex-col justify-end gap-3 overflow-hidden px-2.5 pb-3 pt-4">{children}</div>
  </div>
);
